import React, { useState } from 'react';
import {
  Paper, Box, Typography, TableContainer, Table, TableHead,
  TableBody, TableRow, TableCell, Chip, Button, IconButton, Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Money as MoneyIcon,
  Check as CheckIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../utils/liquidacionHelpers';
import CalendarioAsistencia from './CalendarioAsistencia';

const TIPO_PAGO_CONFIG = {
  COMISION: { label: 'Comisión', color: '#1A237E', bg: '#E8EAF6' },
  SUELDO: { label: 'Sueldo', color: '#2E7D32', bg: '#E8F5E9' },
  JORNAL: { label: 'Jornal', color: '#E65100', bg: '#FFF3E0' },
};

const TablaTrabajadoresPendientes = ({ trabajadoresPendientes = [], onLiquidar, onRefresh }) => {
  const [calendarioOpen, setCalendarioOpen] = useState(false);
  const [calendarioTrabajador, setCalendarioTrabajador] = useState(null);

  const abrirCalendario = (trabajador) => {
    setCalendarioTrabajador(trabajador);
    setCalendarioOpen(true);
  };

  return (
    <>
      <Paper sx={{ mb: 4, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #E2E8F0' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E', display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: '#4CAF50' }} />
            Trabajadores Pendientes
            <Chip label={trabajadoresPendientes.length} size="small" color="success" sx={{ ml: 1 }} />
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Trabajador</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Servicios</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ingresos</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Egresos</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trabajadoresPendientes.map((t) => {
                const tipoCfg = TIPO_PAGO_CONFIG[t.tipo_pago] || TIPO_PAGO_CONFIG.COMISION;
                const tieneAsistencia = t.tipo_pago === 'SUELDO' || t.tipo_pago === 'JORNAL';

                // Calcular ingreso total según tipo
                let ingresoLabel = '';
                let ingresoMonto = 0;
                if (t.tipo_pago === 'SUELDO') {
                  ingresoMonto = parseFloat(t.sueldo_acumulado || 0) + (t.permite_comision_extra ? parseFloat(t.total_comisiones || 0) : 0);
                  ingresoLabel = `Sueldo: ${formatCurrency(t.sueldo_acumulado)}${t.permite_comision_extra && parseFloat(t.total_comisiones) > 0 ? ` + Com: ${formatCurrency(t.total_comisiones)}` : ''}`;
                } else if (t.tipo_pago === 'JORNAL') {
                  ingresoMonto = parseFloat(t.total_jornales || 0) + (t.permite_comision_extra ? parseFloat(t.total_comisiones || 0) : 0);
                  ingresoLabel = `${t.dias_trabajados || 0}d × ${formatCurrency(t.monto_jornal)}${t.permite_comision_extra && parseFloat(t.total_comisiones) > 0 ? ` + Com: ${formatCurrency(t.total_comisiones)}` : ''}`;
                } else {
                  ingresoMonto = parseFloat(t.total_comisiones || 0);
                  ingresoLabel = `${t.servicios_pendientes || 0} servicios`;
                }

                return (
                  <TableRow key={t.id_trabajador} sx={{
                    '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.04)' },
                    borderLeft: `4px solid ${tipoCfg.color}`,
                  }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        {t.nombre_trabajador} {t.apellido_trabajador}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={tipoCfg.label} size="small"
                        sx={{ bgcolor: tipoCfg.bg, color: tipoCfg.color, fontWeight: 700, fontSize: 11 }} />
                    </TableCell>
                    <TableCell align="center">
                      {t.servicios_pendientes > 0 ? (
                        <Chip label={t.servicios_pendientes} size="small" color="primary" />
                      ) : (
                        <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 700, color: '#10B981', fontSize: 14 }}>
                        {formatCurrency(ingresoMonto)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: '#64748B' }}>{ingresoLabel}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{
                        fontWeight: 700, fontSize: 14,
                        color: parseFloat(t.egresos_pendientes) > 0 ? '#EF4444' : '#94A3B8',
                      }}>
                        {formatCurrency(t.egresos_pendientes)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {tieneAsistencia && (
                          <Tooltip title="Ver asistencia">
                            <IconButton size="small" onClick={() => abrirCalendario(t)}
                              sx={{ color: tipoCfg.color, width: 32, height: 32 }}>
                              <CalendarIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Button variant="contained" size="small" startIcon={<MoneyIcon />}
                          onClick={() => onLiquidar(t)}
                          sx={{ background: 'linear-gradient(135deg, #1A237E, #3949AB)', borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 12 }}>
                          Liquidar
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {trabajadoresPendientes.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No hay trabajadores pendientes de liquidar
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Calendario de asistencia */}
      <CalendarioAsistencia
        open={calendarioOpen}
        onClose={() => setCalendarioOpen(false)}
        trabajador={calendarioTrabajador}
        onUpdate={onRefresh}
      />
    </>
  );
};

export default TablaTrabajadoresPendientes;
